import { useEffect } from 'react'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import ErrorIcon from '@material-ui/icons/Error'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { useSubscriptionJobStatus } from '../hooks'
import { useHistory } from 'react-router-dom'
import { constants } from '../utils'

const useStyles = makeStyles(() => ({
  modalLoading: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '32px',
    textAlign: 'center',
    minHeight: '200px',
  },
  modalLoadingSpinner: {
    marginBottom: '24px',
  },
  modalLoadingTitle: {
    fontWeight: 500,
    marginBottom: '8px',
    color: '#1976d2',
    fontSize: '1rem',
  },
  modalLoadingMessage: {
    color: '#666',
    lineHeight: 1.5,
    fontSize: '0.85rem',
  },
  modalFailed: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
    textAlign: 'center',
    minHeight: '160px',
  },
  modalFailedIcon: {
    marginBottom: '16px',
  },
  modalFailedTitle: {
    fontWeight: 600,
    marginBottom: '12px',
    fontSize: '1.1rem',
  },
  modalFailedMessage: {
    color: '#666',
    lineHeight: 1.5,
    fontSize: '0.9rem',
  },
}))

const SubscriptionStatusModal = ({
  open,
  jid,
  onClose,
  onSuccess,
  onError,
  onRenewEnrollmentRequested,
  onFailed,
  setStep,
  retrieveSubscriptionData,
}) => {
  const classes = useStyles()

  const {
    data,
    status,
    isProcessing,
    isSuccess,
    isUnprocessableEntity,
    isRenewEnrollmentRequested,
    isFailed,
    hasError,
    error,
    message,
    isNotFound,
    stopPolling,
  } = useSubscriptionJobStatus(jid, open, 10000, 2000, false)

  const history = useHistory()

  useEffect(() => {
    if (isSuccess && status) {
      stopPolling()
      onSuccess?.(data?.payload)
      retrieveSubscriptionData()

      setTimeout(onClose, 300)
    }
  }, [isSuccess, status, stopPolling, onSuccess, onClose])

  useEffect(() => {
    if (isUnprocessableEntity && status) {
      stopPolling()
      onError?.(error)

      setTimeout(onClose, 300)
    }
  }, [isUnprocessableEntity, status, stopPolling, onError, onClose])

  useEffect(() => {
    if (isRenewEnrollmentRequested && status) {
      stopPolling()
      onRenewEnrollmentRequested?.()
    }
  }, [isRenewEnrollmentRequested, status, stopPolling, onRenewEnrollmentRequested])

  useEffect(() => {
    if (isFailed && status) {
      stopPolling()
      onFailed?.()
    }
  }, [isFailed, status, stopPolling, onFailed])

  const loadingMessage = () => {
    return (
      <Box className={classes.modalLoading}>
        <CircularProgress
          size={40}
          thickness={4}
          className={classes.modalLoadingSpinner}
        />
        <Typography
          variant="h6"
          gutterBottom
          className={classes.modalLoadingTitle}
        >
          Estamos enviando a solicitação.
        </Typography>
        <Typography variant="body1" className={classes.modalLoadingMessage}>
          Aguarde a conclusão.
        </Typography>
      </Box>
    )
  }

  const renderStatusContent = () => {
    if (status) {
      if (isProcessing || isNotFound) {
        return loadingMessage()
      }

      if (isFailed || isUnprocessableEntity) {
        return (
          <Box className={classes.modalFailed}>
            <ErrorIcon
              size={40}
              className={classes.modalFailedIcon}
              color="error"
            />
            <Typography
              variant="h6"
              gutterBottom
              color="error"
              className={classes.modalFailedTitle}
            >
              Não foi possível concluir a solicitação!
            </Typography>
            <Typography variant="body1" className={classes.modalFailedMessage}>
              Clique em <b>Revisar e Tentar Novamente.</b>
              <br />
              Caso o problema persista, entre em contato com a SRE de sua
              região.
            </Typography>
          </Box>
        )
      }

      if (hasError) {
        return (
          <Box p={3}>
            <Typography variant="h6" gutterBottom color="error">
              {error}
            </Typography>
            <Typography
              variant="body1"
              color="error"
              style={{ marginBottom: 16 }}
            >
              {message}
            </Typography>
          </Box>
        )
      }
    } else {
      return loadingMessage()
    }
  }

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogContent>{renderStatusContent()}</DialogContent>

      <DialogActions>
        {(isFailed || isUnprocessableEntity) && (
          <>
            <Button onClick={() => setStep(0)} color="primary">
              Revisar e Tentar Novamente
            </Button>
            <Button
              onClick={() => {
                onClose?.()
                history.push(constants.routes.subscriptions.create.path)
              }}
              color="secondary"
            >
              Sair
            </Button>
          </>
        )}

        {hasError && (
          <Button
            onClick={() => history.push(constants.routes.home.path)}
            color="primary"
          >
            Sair
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default SubscriptionStatusModal
